import { ArrowLeft, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { formatarData } from '@/nucleo/formato/datas';
import { Alert, AlertDescription } from '@/ui/base/alert';
import { Badge } from '@/ui/base/badge';
import { Card, CardContent } from '@/ui/base/card';
import { Input } from '@/ui/base/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/ui/base/select';
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/ui/base/table';
import { EmptyState } from '@/ui/EmptyState';
import { ErrorState } from '@/ui/ErrorState';
import { PaginacaoCompacta } from '@/ui/PaginacaoCompacta';
import { Spinner } from '@/ui/Spinner';

import { mensagemDoErro } from '../parametrizacao/cadastros.dados';
import { ComoFuncionaAIntegracao } from './ComoFuncionaAIntegracao';
import type { FiltrosDaFila, ItemDaFila } from './contrato';
import { DecisaoDaFilaDialogo } from './DecisaoDaFila';
import { TAMANHO_DA_PAGINA_DA_FILA, useFilaDeGrafias } from './dados';
import { ROTULO_DA_PENDENCIA, rotuloDoTipo } from './rotulos';

const HUB = '/admin/parametros/cadastros';
const ESPERA_DA_BUSCA_MS = 300;

/**
 * Fila de conferência das grafias de veículo: os textos de marca e modelo
 * que a fonte oficial publica e que nenhuma grafia nem nome oficial do TEG
 * reconhece. Nenhum deles vira cadastro sozinho; a administração decide, um a
 * um, se é outra grafia de um item existente ou um item novo.
 *
 * A linha inteira abre a decisão (regra 9 de interface): o primeiro campo é o
 * botão, com o nome acessível da linha, e o clique na linha faz o mesmo.
 */
export function FilaDeGrafiasPage() {
  const [digitado, setDigitado] = useState('');
  const [busca, setBusca] = useState('');
  const [pendencia, setPendencia] = useState<FiltrosDaFila['pendencia']>('todas');
  const [pagina, setPagina] = useState(1);
  const [aberto, setAberto] = useState<ItemDaFila | null>(null);

  useEffect(() => {
    const espera = setTimeout(() => {
      setBusca(digitado);
      setPagina(1);
    }, ESPERA_DA_BUSCA_MS);
    return () => clearTimeout(espera);
  }, [digitado]);

  const filtros = useMemo<FiltrosDaFila>(
    () => ({ pagina, busca, pendencia }),
    [pagina, busca, pendencia],
  );
  const consulta = useFilaDeGrafias(filtros);
  const itens = consulta.data?.itens ?? [];
  const total = consulta.data?.total ?? 0;
  const filtrando = busca.trim() !== '' || pendencia !== 'todas';

  return (
    <div className="space-y-6">
      <div>
        <Link
          to={HUB}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
          Cadastros &amp; Domínios
        </Link>
      </div>

      <header className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h1 className="text-2xl font-bold tracking-tight">Fila de Grafias de Veículo</h1>
          <p className="text-sm text-muted-foreground">
            Textos de marca e modelo da fonte oficial que o sistema ainda não reconhece. Decida se
            cada um é outra grafia de um item que já existe ou um item novo.
          </p>
        </div>
        <ComoFuncionaAIntegracao />
      </header>

      {consulta.isLoading && (
        <Card>
          <CardContent className="flex items-center justify-center gap-3 py-16">
            <Spinner />
            <p className="text-sm text-muted-foreground">Carregando a fila…</p>
          </CardContent>
        </Card>
      )}

      {consulta.isError && !consulta.data && (
        <ErrorState
          variant="load-failed"
          title="Não foi possível carregar a fila"
          description={mensagemDoErro(
            consulta.error,
            'Tente de novo. Se continuar, avise a administração do sistema.',
          )}
          primaryAction={{ label: 'Tentar de novo', onClick: () => void consulta.refetch() }}
          secondaryAction={{ label: 'Cadastros & Domínios', to: HUB, variant: 'outline' }}
        />
      )}

      {consulta.data && (
        <div className="space-y-3">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative flex-1">
              <Search
                className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                aria-hidden="true"
              />
              <Input
                type="search"
                value={digitado}
                onChange={(e) => setDigitado(e.target.value)}
                placeholder="Buscar pelo texto da fonte…"
                aria-label="Buscar na fila de grafias"
                className="pl-9"
              />
            </div>
            <Select
              value={pendencia}
              onValueChange={(v) => {
                setPendencia(v as FiltrosDaFila['pendencia']);
                setPagina(1);
              }}
            >
              <SelectTrigger className="sm:w-56" aria-label="Filtrar pelo que falta reconhecer">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todas">Tudo o que falta</SelectItem>
                <SelectItem value="MARCA">{ROTULO_DA_PENDENCIA.MARCA}</SelectItem>
                <SelectItem value="MODELO">{ROTULO_DA_PENDENCIA.MODELO}</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
              {total} {total === 1 ? 'texto na fila' : 'textos na fila'}
            </p>
          </div>

          {consulta.isError && (
            <Alert variant="destructive" role="alert">
              <AlertDescription>
                Não foi possível atualizar a fila. Os dados abaixo podem estar desatualizados.
              </AlertDescription>
            </Alert>
          )}

          {total === 0 && !filtrando ? (
            <EmptyState
              title="Nenhuma grafia esperando conferência"
              description="Todo texto que a fonte oficial publicou foi reconhecido. Quando aparecer grafia nova, ela vem para cá."
            />
          ) : total === 0 ? (
            <EmptyState
              variant="search"
              title="Nenhum texto corresponde"
              description="Nenhum texto da fila atende à busca e ao filtro escolhidos."
              actionLabel="Limpar busca e filtro"
              onAction={() => {
                setDigitado('');
                setBusca('');
                setPendencia('todas');
                setPagina(1);
              }}
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <div className="overflow-x-auto">
                  <Table>
                    <TableCaption className="sr-only">
                      Textos da fonte oficial que esperam conferência
                    </TableCaption>
                    <TableHeader>
                      <TableRow>
                        <TableHead scope="col">Texto na fonte</TableHead>
                        <TableHead scope="col">O que falta</TableHead>
                        <TableHead scope="col">Marca reconhecida</TableHead>
                        <TableHead scope="col">Tipo na fonte</TableHead>
                        <TableHead scope="col" className="text-right">
                          Veículos
                        </TableHead>
                        <TableHead scope="col">Na fonte</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {itens.map((item) => (
                        <TableRow
                          key={item.id}
                          className="cursor-pointer hover:bg-muted/50"
                          onClick={() => setAberto(item)}
                        >
                          <TableCell>
                            <button
                              type="button"
                              className="text-left font-mono font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              aria-label={`Conferir ${item.textoOriginal}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setAberto(item);
                              }}
                            >
                              {item.textoOriginal}
                            </button>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{ROTULO_DA_PENDENCIA[item.pendencia]}</Badge>
                          </TableCell>
                          <TableCell>{item.marcaNome ?? '—'}</TableCell>
                          <TableCell>
                            {item.tiposVeiculo.length
                              ? item.tiposVeiculo.map(rotuloDoTipo).join(', ')
                              : '—'}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {item.quantidade.toLocaleString('pt-BR')}
                          </TableCell>
                          <TableCell>
                            {item.naoVistoDesde
                              ? `Não visto desde ${formatarData(item.naoVistoDesde)}`
                              : `Visto em ${formatarData(item.vistoEm)}`}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          )}

          <PaginacaoCompacta
            pagina={pagina}
            tamanho={TAMANHO_DA_PAGINA_DA_FILA}
            total={total}
            aoMudar={setPagina}
          />
        </div>
      )}

      {aberto && (
        <DecisaoDaFilaDialogo key={aberto.id} item={aberto} aoFechar={() => setAberto(null)} />
      )}
    </div>
  );
}

export default FilaDeGrafiasPage;
